import React from 'react';
import PropTypes from 'prop-types';
import PaginatedTable from 'common/DataTable/PaginatedTable';
import { formatDataForDisplay } from 'common/DataTable/Utils';
import * as Routes from 'routes';
import ReactDOM from 'react-dom';
import moment from 'moment';
import RuleBreakdown from './RuleBreakdown';
import ChangeDate from './ChangeDate';

const ProgramMappingVersionsTable = ({
  isSysadmin, subscriberId, tableHooks, programData, lastSuccessfulSyncTime
}) => {
  const pageSize = 10;

  const {
    resources,
    resetResources,
    addResources,
    doneLoading,
    pagesLoaded,
  } = tableHooks;

  const getUrl = Routes.plato_api_subscriber_program_mapping_versions_path(subscriberId, {
    column: 'columnName',
    includes: [{ subscriber: 'seat_pools' }, 'author'],
    order: 'sortDirection',
    page: 'pageNumber',
    per_page: pageSize
  });

  const formattedResources = resources.map((resource) => {
    const hideEmail = !isSysadmin && resource.author.is_sysadmin;
    const changedBy = hideEmail ? 'TCI' : resource.author.email;

    const program = programData.find(currentProgram => currentProgram.id === resource.current_program_id);
    const programTitle = program && program.title;
    const programCode = program && program.code;

    const previousProgram = programData.find(currentProgram => currentProgram.id === resource.previous_program_id);
    const previousProgramTitle = previousProgram && previousProgram.title;

    return {
      ...resource,
      changed_by: changedBy,
      previous_program_name: previousProgramTitle || '',
      program_code: programCode || '',
      program_name: programTitle || '',
    };
  });

  return (
    <div className="mt80">
      <h2>Recent Changes</h2>
      <PaginatedTable
        handleResourcesManually
        addResources={addResources}
        columnDefs={[
          {
            createdCell: (td, cellData, rowData) => {
              ReactDOM.render(
                <RuleBreakdown programData={programData} versionData={rowData} />,
                td
              );
            },
            render: currentRules => (currentRules || []).join(' AND '),
            targets: 0,
            title: 'Rule'
          },
          {
            targets: 1,
            width: '200px'
          },
          {
            targets: 2,
            width: '75px'
          },
          {
            targets: 3,
            width: '55px'
          },
          {
            targets: 4,
            width: '60px'
          },
          {
            targets: 5,
            width: '120px'
          },
          {
            createdCell: (td, _cellData, rowData) => {
              ReactDOM.render(
                <ChangeDate
                  changeDate={rowData.created_at}
                  lastSuccessfulSyncTime={lastSuccessfulSyncTime}
                />,
                td
              );
            },
            isDate: true,
            render: formatDataForDisplay(changeDate => moment(changeDate).format('MMM D, YYYY')),
            targets: 6,
            title: 'Change Date',
            width: '65px'
          },
          {
            render: previousRules => (previousRules || []).join(' AND '),
            targets: 7,
            title: 'Previous Rule',
            visible: false
          },
          {
            targets: 8,
            title: 'Previous Program',
            visible: false
          }
        ]}
        columnMapping={[]}
        columns={[
          'current_rules', 'program_name', 'program_code', 'grade', 'change_type', 'changed_by', 'created_at',
          'previous_rules', 'previous_program_name'
        ]}
        defaultOrder={[[6, 'desc'], [4, 'asc'], [1, 'asc']]}
        doneLoading={doneLoading}
        exportedColumns={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
        getUrl={getUrl}
        pageLength={pageSize}
        pagesLoaded={pagesLoaded}
        resetResources={resetResources}
        resources={formattedResources}
      />
    </div>
  );
};

ProgramMappingVersionsTable.propTypes = {
  isSysadmin: PropTypes.bool.isRequired,
  lastSuccessfulSyncTime: PropTypes.string,
  programData: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    id: PropTypes.number,
    title: PropTypes.string
  })).isRequired,
  subscriberId: PropTypes.number.isRequired,
  tableHooks: PropTypes.shape({
    addResources: PropTypes.func.isRequired,
    doneLoading: PropTypes.bool.isRequired,
    pagesLoaded: PropTypes.number.isRequired,
    resetResources: PropTypes.func.isRequired,
    resources: PropTypes.array.isRequired
  }).isRequired
};

ProgramMappingVersionsTable.defaultProps = {
  lastSuccessfulSyncTime: null
};

export default ProgramMappingVersionsTable;
